// Host-agnostic chat session (PLAN-agent-v2 §5.2): consumes the /agent-v2 SSE stream, turns
// `client_tool_call` batches into cards, waits for the user's decisions (policy #5), runs the
// approved calls through the ToolRunner, posts the results to /resume and consumes the next
// stream with the same reducer. React binds to it through `useAgentChat` (useSyncExternalStore).
import { APPROVE_BATCH, isSubAgent, MODEL_NOT_ALLOWED_CODE, TOOL_ASK_USER, TURNS_IN_PROGRESS_CODE, type TurnsInProgressDetail, type AgentContext, type ChatMessage, type ClientToolBatch, type ClientToolCall, type DisplayDocument, type DisplayMessage, type DisplayToolStatus, type MessageSegment, type ManualDeclaration, type Mode, type PlanView, type QuestionAnswer, type SSEEvent, type ServerToolResult, type ThreadDetail, type ToolCallStarted, type ToolCard, type ToolResult, type ToolResultEntry, type TurnActivity, type TurnRating, type TurnStarted, type UndoRecord, type UsageSummary, type ResumeRequest, type AuditView } from '../../types/agent';

/** `_changes` of a write result, when present and well-formed. */
function changesOf(result: Record<string, unknown>): ToolCard['changes'] {
  const c = result._changes as { sheet?: unknown; address?: unknown; cells?: unknown } | undefined;
  if (!c || typeof c.sheet !== 'string' || typeof c.address !== 'string') return null;
  return { sheet: c.sheet, address: c.address, cells: typeof c.cells === 'number' ? c.cells : 0 };
}
import { needsApproval } from './policy';
import { CANCELLED_RESULT, type ToolExecution, type ToolRunner } from './ToolRunner';
import { TOOL_CATALOGUE, toolTarget } from './toolCatalogue';
import { isToolError } from '../excel/officeErrors';
import { touchedCircularBlocks, type CircularBlock } from '../excel/convergence';
import { INDEX_REQUIRED_CODES, INDEX_UNDERSTANDING_CODE } from '../../types/index';

export interface ChatSessionDeps {
  api: {
    chat(body: { message: string; thread_id: string | null; mode: Mode; context: AgentContext; document_ids?: string[]; model?: string }, signal?: AbortSignal): AsyncGenerator<SSEEvent>;
    resume(body: ResumeRequest, signal?: AbortSignal): AsyncGenerator<SSEEvent>;
    cancelThread(threadId: string): Promise<void>;
    getThread(threadId: string): Promise<ThreadDetail>;
    /** Wake a run the server cut short (`stalled` event); optional so tests and older mocks keep working. */
    continueThread?: (threadId: string, signal?: AbortSignal) => AsyncGenerator<SSEEvent>;
    /** Persisted undo (domain #38f); optional so tests and older mocks keep working. */
    saveUndo?: (record: Omit<UndoRecord, 'stale' | 'id' | 'created_at'> & { thread_id: string; workbook_id?: string | null }) => Promise<void>;
    listUndo?: (threadId: string, fingerprints?: Record<string, string | null>, lastN?: number, workbookId?: string | null) => Promise<UndoRecord[]>;
    markUndone?: (threadId: string, journalId: string) => Promise<void>;
    /** How long the agent took on a turn, kept with the thread; optional so tests and older mocks keep working. */
    recordTurnTime?: (threadId: string, turnIndex: number, durationMs: number) => Promise<void>;
  };
  runner: Pick<ToolRunner, 'runSequential' | 'undoEntry'> & Partial<Pick<ToolRunner, 'journalEntry' | 'restoreFromRecord' | 'previewCall'>>;
  /** Live fingerprint of one sheet (undo write-through / restore guard); optional in tests. */
  sheetFingerprint?: (sheet: string) => Promise<string | null>;
  /** Current workbook id for the persisted undo records; optional in tests. */
  getWorkbookId?: () => string | null;
  /** Circular modules of the workbook (domain #35): converged automatically after a write touches one. */
  circularBlocks?: () => Promise<CircularBlock[]>;
  /** Runs one local tool call outside a batch (the auto-convergence). */
  runLocal?: (toolName: string, input: Record<string, unknown>) => Promise<ToolResult>;
  /** The sheets a batch has just written in, so the next index captures them whatever their fingerprint
   *  says: the fingerprint does not see formats, styles or recalculated values. */
  onSheetsWritten?: (sheets: string[]) => void;
  /** Whether the agent is working on a turn: a stream is open, or the batch it sent is running. A batch or a
   *  Question waiting on the user is not working. The index catches up once it stops, not after each batch. */
  onAgentWorking?: (working: boolean) => void;
  /** The server refused a turn before it started because the index is missing or stale (409
   *  `workbook_not_indexed` / `index_stale`): rebuild it. Resolves null when the turn can be sent again, else
   *  the message to show. Without it the refusal is reported as an error. */
  refreshIndex?: () => Promise<string | null>;
  /** The server refused a turn before it started because it has not finished understanding the version it
   *  serves (409 `index_not_understood`: the catalog of a version just committed is still being built, and every
   *  version waits for its own, sculpt-api D28): wait for that pass, nothing to rebuild. Same contract as
   *  `refreshIndex`; without it the refusal is reported as an error. */
  awaitUnderstanding?: () => Promise<string | null>;
  onConverged?: (info: { block_id: string; result: ToolResult }) => void;
  buildContext: () => Promise<AgentContext>;
  getMode: () => Mode;
  /** The model a new thread runs on (PLAN-model-choice.md); null or absent: the server's default. */
  getModel?: () => string | null;
  /** The server refused the turn's model (403 `model_not_allowed`): the message to show. Without it, a default. */
  onModelRefused?: () => string;
  /** The server refused the turn because the user already has `limit` turns streaming (429 `turns_in_progress`):
   *  the message to show. Without it, a default. */
  onTurnsInProgress?: (limit: number) => string;
  /** The model provider cannot serve the turn (`service_interrupted`: its account ran out of credit, staff already
   *  told by the server): the message to show. Without it, a default. */
  onServiceInterrupted?: () => string;
  onUsage?: (usage: UsageSummary) => void;
  onThreadChanged?: (threadId: string | null) => void;
  /** An audit card changed what it says is fixed (a turn drew one, an undo opened fixes again): bring the workbook's
   *  Audit Findings sheet in line with it, cell by cell. Resolves true when the sheet was there and some of its rows
   *  changed, false otherwise. */
  onAuditCard?: (view: AuditView) => Promise<boolean>;
}

export interface ChatState {
  threadId: string | null;
  messages: ChatMessage[];
  /** An SSE stream is being consumed. */
  streaming: boolean;
  /** Client tools are executing or awaiting the user's decision. */
  busy: boolean;
  /** A Question card is waiting for the user: the composer stays open, and what they type answers it. */
  awaitingAnswers: boolean;
  error: string | null;
}

type Listener = () => void;

let idSeq = 0;
const nextId = () => `m${++idSeq}`;

/** A 403 whose code says the organization refuses the model of the turn. */
function isModelRefusal(err: unknown): boolean {
  const e = err as { status?: unknown; body?: unknown } | null;
  if (!e || e.status !== 403 || typeof e.body !== 'string') return false;
  return safeParse<{ detail?: { code?: string } }>(e.body)?.detail?.code === MODEL_NOT_ALLOWED_CODE;
}

/** The user's limit of turns streaming at once when the server refused this one for it (429 `turns_in_progress`,
 *  before the stream starts), else null. */
function turnsInProgressLimit(err: unknown): number | null {
  const e = err as { status?: unknown; body?: unknown } | null;
  if (!e || e.status !== 429 || typeof e.body !== 'string') return null;
  const detail = safeParse<{ detail?: Partial<TurnsInProgressDetail> }>(e.body)?.detail;
  if (detail?.code !== TURNS_IN_PROGRESS_CODE) return null;
  return typeof detail.limit === 'number' ? detail.limit : 0;
}

/** What a turn refused for its index waits for before it is sent again: `rebuild` when the workbook must be
 *  (re-)indexed, `understanding` when the server has not finished its pass on the version it serves. */
type IndexWait = 'rebuild' | 'understanding';
/** The waits a turn may still answer a refusal with (`consume`, `sendAfterIndex`). */
type IndexWaits = Record<IndexWait, boolean>;
const NO_INDEX_WAIT: IndexWaits = { rebuild: false, understanding: false };

/** The wait a 409 asks for, when its code is one of the index's; null for any other error. */
function indexRefusalOf(err: unknown): IndexWait | null {
  // Duck-typed `ApiError` (status + body): the session stays free of the HTTP module.
  const e = err as { status?: unknown; body?: unknown } | null;
  if (!e || e.status !== 409 || typeof e.body !== 'string') return null;
  const code = safeParse<{ detail?: { code?: string } }>(e.body)?.detail?.code ?? '';
  if ((INDEX_REQUIRED_CODES as readonly string[]).includes(code)) return 'rebuild';
  return code === INDEX_UNDERSTANDING_CODE ? 'understanding' : null;
}

/** The turns whose fixes a newer audit card opened again: a finding fixed in an older card, by turn T, that the
 *  newer card (same id and title) shows open. */
function turnsReopenedBy(messages: ChatMessage[], card: AuditView): number[] {
  const key = (f: { id: string; title: string }) => `${f.id}\u0000${f.title}`;
  const open = new Set(card.findings.filter((f) => !f.fixed).map(key));
  const turns = messages.flatMap((m) => m.audit?.findings ?? []).filter((f) => f.fixed && f.fixed_in != null && open.has(key(f)));
  return [...new Set(turns.map((f) => f.fixed_in as number))];
}

function safeParse<T>(s: string): T | null {
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

// ---- segments (ordered timeline of a turn) --------------------------------------------

/** Append text to the last text segment, or open a new one when the last segment is tools. */
function appendText(m: ChatMessage, text: string): ChatMessage {
  const segs = [...(m.segments ?? [])];
  const last = segs[segs.length - 1];
  if (last && last.kind === 'text') segs[segs.length - 1] = { kind: 'text', markdown: last.markdown + text };
  else segs.push({ kind: 'text', markdown: text });
  return { ...m, content: m.content + text, segments: segs };
}

/** Append tool cards to the last tools segment, or open a new one when the last segment is text.
 *  Cards replacing an existing id (server tool_call re-emitted) stay in place. */
function appendCards(m: ChatMessage, cards: ToolCard[]): ChatMessage {
  let segs = [...(m.segments ?? [])];
  let all = [...(m.toolCards ?? [])];
  for (const card of cards) {
    const existing = all.findIndex((c) => c.tool_call_id === card.tool_call_id);
    if (existing >= 0) {
      all[existing] = card;
      segs = segs.map((seg) => (seg.kind === 'tools' ? { kind: 'tools', cards: seg.cards.map((c) => (c.tool_call_id === card.tool_call_id ? card : c)) } : seg));
      continue;
    }
    all = [...all, card];
    const last = segs[segs.length - 1];
    if (last && last.kind === 'tools') segs[segs.length - 1] = { kind: 'tools', cards: [...last.cards, card] };
    else segs.push({ kind: 'tools', cards: [card] });
  }
  return { ...m, toolCards: all, segments: segs };
}

/** An approval card with one of its edits settled: what it wrote, or that it failed. */
function withEditResult(card: ToolCard, exec: ToolExecution): ToolCard {
  const edits = (card.input as { edits?: unknown }).edits;
  if (!Array.isArray(edits)) return card;
  const settled = (e: ToolCard): ToolCard => ({ ...e, status: isToolError(exec.result) ? 'error' : 'done', changes: changesOf(exec.result) });
  return { ...card, input: { ...card.input, edits: (edits as ToolCard[]).map((e) => (e.tool_call_id === exec.tool_call_id ? settled(e) : e)) } };
}

/** The message without these cards (segments + flat list); a segment left empty goes with them. */
function removeCards(m: ChatMessage, ids: Set<string>): ChatMessage {
  const keep = (c: ToolCard) => !ids.has(c.tool_call_id);
  const segments = (m.segments ?? [])
    .map((seg) => (seg.kind === 'tools' ? { kind: 'tools' as const, cards: seg.cards.filter(keep) } : seg))
    .filter((seg) => seg.kind !== 'tools' || seg.cards.length > 0);
  return { ...m, toolCards: (m.toolCards ?? []).filter(keep), segments };
}

/** Apply `fn` to one card wherever it lives (segments + flat list). */
function mapCard(m: ChatMessage, toolCallId: string, fn: (c: ToolCard) => ToolCard): ChatMessage {
  const upd = (c: ToolCard) => (c.tool_call_id === toolCallId ? fn(c) : c);
  return {
    ...m,
    toolCards: (m.toolCards ?? []).map(upd),
    segments: (m.segments ?? []).map((seg) => (seg.kind === 'tools' ? { kind: 'tools', cards: seg.cards.map(upd) } : seg)),
  };
}

/** Apply `fn` to every card of a turn. */
function mapCards(m: ChatMessage, fn: (c: ToolCard) => ToolCard): ChatMessage {
  return {
    ...m,
    toolCards: (m.toolCards ?? []).map(fn),
    segments: (m.segments ?? []).map((seg) => (seg.kind === 'tools' ? { kind: 'tools', cards: seg.cards.map(fn) } : seg)),
  };
}

/** A batch that asks rather than acts: one `ask_user` call the user answers in a card (sculpt-api
 *  PLAN-questions.md). The tool name is checked too, so a server that does not send `kind` still works. */
export function isQuestionBatch(batch: ClientToolBatch): boolean {
  return batch.kind === 'questions' || (batch.calls.length === 1 && batch.calls[0].tool_name === TOOL_ASK_USER);
}

/** Sub-agent currently active in a turn (a running / pending / awaiting card issued by it), if any. */
export function activeAgent(m: ChatMessage): string | null {
  for (let i = (m.toolCards ?? []).length - 1; i >= 0; i--) {
    const c = m.toolCards![i];
    if ((c.status === 'running' || c.status === 'pending' || c.status === 'awaiting_confirmation') && isSubAgent(c.agent)) return c.agent;
  }
  return null;
}

/** Whether the turn is waiting on the user: a card to approve, or a Question to answer. */
function waitsOnUser(m: ChatMessage): boolean {
  return !!m.pending && !!m.toolCards?.some((c) => c.status === 'awaiting_confirmation');
}

/** Moves the turn's clock from `prev` to `next` at `now`: it pauses while the turn waits on the user, and
 *  stops for good when the turn ends, keeping how long the agent took (`turnMs`). */
export function turnClock(prev: ChatMessage, next: ChatMessage, now: number): ChatMessage {
  if (next.turnStartedAt == null || next.turnMs != null) return next;
  let { userWaitMs = 0, userWaitSince } = next;
  const waiting = waitsOnUser(next);
  if (waiting && userWaitSince == null) userWaitSince = now;
  if (!waiting && userWaitSince != null) {
    userWaitMs += now - userWaitSince;
    userWaitSince = undefined;
  }
  const turnMs = prev.pending && !next.pending ? Math.max(0, now - next.turnStartedAt - userWaitMs) : undefined;
  if (userWaitMs === (next.userWaitMs ?? 0) && userWaitSince === next.userWaitSince && turnMs === undefined) return next;
  return { ...next, userWaitMs, userWaitSince, ...(turnMs !== undefined ? { turnMs } : {}) };
}

/** How long the turn has been running at `now`, user waits left out; the frozen `turnMs` once it has ended. */
export function turnElapsed(m: ChatMessage, now: number): number | null {
  if (m.turnMs != null) return m.turnMs;
  if (m.turnStartedAt == null) return null;
  const waiting = m.userWaitSince != null ? now - m.userWaitSince : 0;
  return Math.max(0, now - m.turnStartedAt - (m.userWaitMs ?? 0) - waiting);
}

/** Whether a card is still shown once settled: action cards (needed approval) always are, plain steps only
 *  while they are still going.
 *
 *  A failed step used to be permanent. But a step that fails is usually a step the agent is about to try
 *  another way — `start_add` refused because the object it guessed does not exist, a `read_zone` on an id
 *  that moved — and the turn goes on to succeed with a red row sitting above it saying otherwise. It stays
 *  long enough to read (`ERROR_HOLD_MS`) and then leaves like any other, so what remains is what the turn
 *  actually did. A reloaded thread never shows it at all: the retry is history, and the answer is the
 *  record.
 *
 *  Action cards (a write the user approved) settle the same way: a failed one shows its error for
 *  `ERROR_HOLD_MS`, then leaves (`ActionCards`); a batch drops its failed calls from its receipt
 *  (`BatchProgress`). No tool error stays on screen. */
export function isCardVisible(c: ToolCard): boolean {
  if (c.requires_confirmation) return true;
  return c.status === 'running' || c.status === 'pending' || c.status === 'awaiting_confirmation';
}

/** Re-attach an event already pulled off a stream, so the reducer sees the whole of it. */
async function* withFirst(first: SSEEvent, rest: AsyncGenerator<SSEEvent>): AsyncGenerator<SSEEvent> {
  yield first;
  yield* rest;
}

/** How many times one turn is woken after the server's silence abort before giving up. */
export const MAX_WAKES_PER_TURN = 2;
/** Before-images posted at once: a batch's all together took the server's connections with them (sculpt-api
 *  `db.release`; a local test lost 17 of one batch), and an undo needs them. */
const UNDO_FILED_AT_ONCE = 4;
/** How long a batch's results wait for its before-images, which go on being filed past it. Enough for a batch of a
 *  few dozen writes on a large sheet (26 s were measured with one read of the sheet per write). */
export const UNDO_FILING_WAIT_MS = 15_000;
const UNDO_FILING_RETRY_MS = 500;
const UNDO_NOT_FILED_TEXT = 'This change was written, but what it takes to undo it could not be saved: it may not be possible to take it back as a whole.';
const STALLED_TEXT = 'The assistant stopped responding. Send your message again to continue.';
const MODEL_REFUSED_TEXT = 'Your organization no longer allows the model of this conversation. Start a new conversation.';
const SERVICE_INTERRUPTED_TEXT = 'The assistant is temporarily unavailable. Our team is already working on it: please try again a little later.';
const turnsInProgressText = (limit: number) => `You already have ${limit} replies running in other workbooks. Wait for one to finish, then send your message again.`;

export class ChatSession {
  private state: ChatState = { threadId: null, messages: [], streaming: false, busy: false, awaitingAnswers: false, error: null };
  private listeners = new Set<Listener>();
  private abort: AbortController | null = null;
  /** Wakes (`/continue` after a `stalled` event) used by the current turn. */
  private wakes = 0;
  /** Set by `consume` when the server refused the turn for its index: what the turn waits for before it goes
   *  again (`refreshIndex` / `awaitUnderstanding`). */
  private indexRefused: IndexWait | null = null;
  /** Batch waiting for decisions: tool_call_id → approved (true) / rejected (false) / undecided. */
  private pending: { batch: ClientToolBatch; decisions: Map<string, boolean | undefined>; acks: Map<string, boolean>; aggregate?: string;
                     /** An approval batch: the calls' steps, drawn only once the edits are accepted. */
                     steps?: ToolCard[];
                     /** What the user wrote when they rejected the edits, sent back with every cancelled call. */
                     declined?: string | null } | null = null;
  /** Question card waiting for the user's answers, with whatever is filled in so far (sculpt-api
   *  PLAN-questions.md): typing in the chat box answers the card, and takes the half-filled fields with it. */
  private questions: { batch: ClientToolBatch; call: ClientToolCall; draft: QuestionAnswer[] } | null = null;

  constructor(private readonly deps: ChatSessionDeps) {}

  // ---- store binding ---------------------------------------------------------------
  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  getState(): ChatState {
    return this.state;
  }

  private set(patch: Partial<ChatState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
    this.reportWorking();
  }

  private working = false;

  /** Streaming, or busy with a batch the user has nothing left to decide on. */
  private reportWorking(): void {
    const waitingOnUser = !!this.questions || (!!this.pending && [...this.pending.decisions.values()].some((d) => d === undefined));
    const working = this.state.streaming || (this.state.busy && !waitingOnUser);
    if (working === this.working) return;
    this.working = working;
    this.deps.onAgentWorking?.(working);
  }

  private patchLastAssistant(fn: (m: ChatMessage) => ChatMessage): void {
    const msgs = [...this.state.messages];
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'assistant') {
        const prev = msgs[i];
        msgs[i] = turnClock(prev, fn(prev), Date.now());
        if (prev.turnMs == null && msgs[i].turnMs != null) this.reportTurnTime(msgs[i]);
        break;
      }
    }
    this.set({ messages: msgs });
  }

  /** The turn has ended: its time goes to the server, so the history shows it under the turn as this pane does.
   *  A turn the server never keyed (no `turn` event) is not reported. */
  private reportTurnTime(m: ChatMessage): void {
    const threadId = this.state.threadId;
    if (!threadId || m.turnIndex == null || m.turnMs == null || !this.deps.api.recordTurnTime) return;
    this.deps.api.recordTurnTime(threadId, m.turnIndex, m.turnMs).catch((err) => console.warn('[agent-v2] turn time not recorded', err));
  }

  /** The Audit Findings sheet, when the workbook has one, brought in line with the card `m` holds, and the card says
   *  so when a row changed. A failure leaves the sheet as it was. */
  private syncAuditSheet(m: ChatMessage | undefined): void {
    if (!m?.audit || !this.deps.onAuditCard) return;
    const id = m.id;
    this.deps.onAuditCard(m.audit)
      .then((updated) => {
        if (updated) this.set({ messages: this.state.messages.map((m) => (m.id === id ? { ...m, auditSheetUpdated: true } : m)) });
      })
      .catch((err) => console.warn('[agent-v2] Audit Findings sheet not updated', err));
  }

  private patchCard(toolCallId: string, fn: (c: ToolCard) => ToolCard): void {
    this.patchLastAssistant((m) => mapCard(m, toolCallId, fn));
  }

  private setActivity(activity: TurnActivity): void {
    this.patchLastAssistant((m) => (m.activity === activity ? m : { ...m, activity }));
  }

  // ---- public actions ---------------------------------------------------------------
  /** Send a user message; `documents` = the already-uploaded attachments of this message (2026-09-10),
   *  referenced by id in the request and shown in the user bubble. */
  async send(text: string, documents: DisplayDocument[] = []): Promise<void> {
    const message = text.trim();
    if (!message || this.state.streaming) return;
    // A Question is waiting: these are the user's own words about it, not a new request. Sending them as a
    // new turn would cancel the card as superseded and, once a Question can interrupt an operation, throw
    // that operation away (PLAN-questions.md D10).
    if (this.questions) {
      await this.answerQuestions(this.questions.draft, message);
      return;
    }
    if (this.state.busy) return;
    // The message shows the moment it is sent, before the workbook is read for its context: that read takes
    // seconds on a large model (every sheet's fingerprint), and an empty pane meanwhile looks like nothing
    // happened. The turn is on from here: the composer waits and Stop stops it.
    const abort = new AbortController();
    this.abort = abort;
    this.wakes = 0;
    this.set({
      error: null,
      streaming: true,
      messages: [...this.state.messages, { id: nextId(), role: 'user', content: message, ...(documents.length ? { documents } : {}) }, { id: nextId(), role: 'assistant', content: '', pending: true, activity: 'context', segments: [], toolCards: [], turnStartedAt: Date.now() }],
    });
    let context: AgentContext;
    try {
      context = await this.deps.buildContext();
    } catch (err) {
      if (!abort.signal.aborted) this.failTurn(err instanceof Error ? err.message : String(err));
      return;
    }
    // Stopped, or another thread opened, while the workbook was being read: the message never leaves.
    if (abort.signal.aborted) return;
    const model = this.state.threadId ? null : this.deps.getModel?.() ?? null;
    const body = { message, thread_id: this.state.threadId, mode: this.deps.getMode(), context, ...(documents.length ? { document_ids: documents.map((d) => d.document_id) } : {}), ...(model ? { model } : {}) };
    const allowed: IndexWaits = { rebuild: true, understanding: true };
    await this.consume(this.deps.api.chat(body, abort.signal), allowed);
    await this.sendAfterIndex(body, allowed);
  }

  /** The turn ended before the server ran it: the message stays in its bubble, the reason under it. */
  private failTurn(msg: string): void {
    this.patchLastAssistant((m) => ({ ...(m.content ? m : appendText(m, msg)), error: true, pending: false, activity: 'idle' }));
    this.set({ streaming: false, error: msg });
  }

  /** The turn was refused before anything ran for its index: the message waits in its bubble while the index is
   *  rebuilt, or while the server finishes understanding it, then goes again with a context read after the wait.
   *  Each wait once per turn — a rebuild commits a version the server then understands, so the wait for that pass
   *  is allowed once more after one — and a refusal past that is reported. */
  private async sendAfterIndex(body: Parameters<ChatSessionDeps['api']['chat']>[0], allowed: IndexWaits): Promise<void> {
    while (this.indexRefused) {
      const wait = this.indexRefused;
      this.indexRefused = null;
      allowed[wait] = false;
      if (wait === 'rebuild') allowed.understanding = true;
      // The turn's own controller: once Stop has ended it, a newer turn's controller must not send this one.
      const abort = this.abort;
      let problem: string | null;
      // The turn is still on until it is sent again: the composer waits with it, Stop still stops it.
      this.set({ streaming: true });
      try {
        problem = await (wait === 'rebuild' ? this.deps.refreshIndex! : this.deps.awaitUnderstanding!)();
      } catch (err) {
        problem = err instanceof Error ? err.message : String(err);
      }
      if (abort?.signal.aborted) return;
      if (problem !== null) return this.failTurn(problem);
      this.patchLastAssistant((m) => ({ ...m, activity: 'context' }));
      let context: AgentContext;
      try {
        context = await this.deps.buildContext();
      } catch (err) {
        if (!abort?.signal.aborted) this.failTurn(err instanceof Error ? err.message : String(err));
        return;
      }
      if (abort?.signal.aborted) return;
      this.patchLastAssistant((m) => ({ ...m, activity: 'model' }));
      this.abort = new AbortController();
      await this.consume(this.deps.api.chat({ ...body, context }, this.abort.signal), allowed);
    }
  }

  /** The card's fields as they stand, so a typed reply carries them (PLAN-questions.md D10). */
  setQuestionDraft(answers: QuestionAnswer[]): void {
    if (this.questions) this.questions.draft = answers;
  }

  decide(toolCallId: string, approved: boolean, opts?: { overwrite_ack?: boolean; message?: string }): void {
    if (this.pending?.aggregate && toolCallId === this.pending.aggregate) {
      // One card for the whole batch (an undo, a batch of edits): its decision is every call's decision.
      const steps = this.pending.steps;
      if (approved && steps?.length) this.patchLastAssistant((m) => appendCards(m, steps));
      if (!approved) this.pending.declined = opts?.message?.trim() || null;
      for (const id of this.pending.decisions.keys()) {
        this.pending.decisions.set(id, approved);
        this.patchCard(id, (c) => ({ ...c, status: approved ? 'pending' : 'cancelled' }));
      }
      this.patchCard(toolCallId, (c) => ({ ...c, status: approved ? 'done' : 'cancelled' }));
      void this.executeIfDecided();
      return;
    }
    if (!this.pending || !this.pending.decisions.has(toolCallId)) return;
    if (opts?.overwrite_ack) this.pending.acks.set(toolCallId, true);
    this.pending.decisions.set(toolCallId, approved);
    this.patchCard(toolCallId, (c) => ({ ...c, status: approved ? 'pending' : 'cancelled' }));
    void this.executeIfDecided();
  }

  decideAll(approved: boolean): void {
    if (!this.pending) return;
    if (this.pending.aggregate) {
      this.decide(this.pending.aggregate, approved);
      return;
    }
    const needsAck = new Set(this.state.messages.flatMap((m) => m.toolCards ?? []).filter((c) => (c.overwrite?.cells ?? 0) > 0).map((c) => c.tool_call_id));
    for (const [id, d] of this.pending.decisions) {
      // An overwrite is never approved wholesale: its card keeps waiting for the explicit acknowledgement (#36).
      if (approved && needsAck.has(id)) continue;
      if (d === undefined) {
        this.pending.decisions.set(id, approved);
        this.patchCard(id, (c) => ({ ...c, status: approved ? 'pending' : 'cancelled' }));
      }
    }
    void this.executeIfDecided();
  }

  /** Stop the turn: abort the stream / execution and resolve a pending interrupt server-side. */
  async stop(): Promise<void> {
    this.abort?.abort();
    const hadPending = !!this.pending || !!this.questions;
    this.pending = null;
    this.questions = null;
    this.patchLastAssistant((m) => ({
      ...mapCards(m, (c) => (c.status === 'pending' || c.status === 'awaiting_confirmation' || c.status === 'running' ? { ...c, status: 'cancelled' } : c)),
      pending: false,
      activity: 'idle',
      pendingBatch: null,
    }));
    this.set({ streaming: false, busy: false, awaitingAnswers: false });
    if (hadPending && this.state.threadId) {
      try {
        await this.deps.api.cancelThread(this.state.threadId);
      } catch (err) {
        console.warn('[agent-v2] cancel failed', err);
      }
    }
  }

  newThread(): void {
    this.abort?.abort();
    this.pending = null;
    this.questions = null;
    this.set({ threadId: null, messages: [], streaming: false, busy: false, awaitingAnswers: false, error: null });
    this.deps.onThreadChanged?.(null);
  }

  /** The user rated a turn (null: took the rating back). Kept on every assistant message of the turn, so the thumbs
   *  and the comment form show what was sent, whichever bubble they hang under and however often it re-renders. */
  noteRating(turnIndex: number, rating: TurnRating | null): void {
    const messages = this.state.messages.map((m) => (m.role === 'assistant' && m.turnIndex === turnIndex ? { ...m, rating: rating ?? undefined } : m));
    this.set({ messages });
  }

  /** Open a saved thread; if it is interrupted, re-attach the pending batch (§5.2 restore). */
  async loadThread(threadId: string): Promise<void> {
    this.abort?.abort();
    this.pending = null;
    this.questions = null;
    const detail = await this.deps.api.getThread(threadId);
    const messages = withRatings(latestCardsPerTurn(detail.messages.map(displayToChat)), detail.ratings ?? []);
    this.set({ threadId: detail.thread_id, messages, streaming: false, busy: false, awaitingAnswers: false, error: null });
    this.deps.onThreadChanged?.(detail.thread_id);
    if (detail.pending_batch && detail.pending_batch.calls.length) {
      if (!messages.length || messages[messages.length - 1].role !== 'assistant') {
        this.set({ messages: [...this.state.messages, { id: nextId(), role: 'assistant', content: '', segments: [], toolCards: [] }] });
      }
      this.abort = new AbortController();
      if (await this.resumeIfStale(detail.pending_batch)) return;
      this.attachBatch(detail.pending_batch);
      await this.executeIfDecided();
    }
  }

  /** Card "Undo" button: revert one journalled change. After a pane reload the in-memory journal is
   *  empty: fall back to the before-image persisted server-side (domain #38f), refusing when the sheet's
   *  fingerprint moved on since the write. */
  async undoCard(toolCallId: string): Promise<void> {
    const card = this.state.messages.flatMap((m) => m.toolCards ?? []).find((c) => c.tool_call_id === toolCallId);
    if (!card?.journal_id) return;
    let r = await this.deps.runner.undoEntry(card.journal_id);
    if (!isToolError(r) && r.status === 'nothing_to_undo') r = await this.undoFromServer(card.journal_id, card.target?.sheet ?? null);
    if (!isToolError(r) && r.status !== 'nothing_to_undo') {
      this.patchAnyCard(toolCallId, (c) => ({ ...c, journal_id: null, undone: true }));
      this.reopenFixes(this.state.messages.find((m) => m.toolCards?.some((c) => c.tool_call_id === toolCallId))?.turnIndex);
      // The restore is the add-in's own write: no change event reports it to the index.
      const sheet = card.changes?.sheet ?? card.target?.sheet;
      if (sheet) this.deps.onSheetsWritten?.([sheet]);
      if (this.state.threadId) void this.deps.api.markUndone?.(this.state.threadId, card.journal_id).catch(() => undefined);
    }
  }

  /** A write of turn `turn` was undone: the audit fixes that turn marked are open again in every card (the server
   *  applies the same rule from the journal, `report.reopen`), and the Audit Findings sheet follows the latest card. */
  private reopenFixes(turn: number | undefined, sync = true): void {
    if (turn == null) return;
    const reopened = (m: ChatMessage): ChatMessage => (m.audit?.findings.some((f) => f.fixed && f.fixed_in === turn)
      ? { ...m, audit: { ...m.audit, findings: m.audit.findings.map((f) => (f.fixed && f.fixed_in === turn ? { ...f, fixed: null, fixed_in: null } : f)) } }
      : m);
    const before = this.state.messages;
    const messages = before.map(reopened);
    if (messages.every((m, i) => m === before[i])) return;
    this.set({ messages });
    if (sync) this.syncAuditSheet([...messages].reverse().find((m) => m.audit));
  }

  private async undoFromServer(journalId: string, sheet: string | null): Promise<ToolResult> {
    const threadId = this.state.threadId;
    const api = this.deps.api;
    const runner = this.deps.runner;
    if (!threadId || !api.listUndo || !runner.restoreFromRecord) return { status: 'nothing_to_undo', restored: [], skipped: [] };
    const fps: Record<string, string | null> = {};
    if (sheet && this.deps.sheetFingerprint) fps[sheet] = await this.deps.sheetFingerprint(sheet).catch(() => null);
    const records = await api.listUndo(threadId, fps, 50, this.deps.getWorkbookId?.() ?? null).catch(() => [] as UndoRecord[]);
    const rec = records.find((x) => x.journal_id === journalId && !x.undone);
    if (!rec) return { status: 'nothing_to_undo', restored: [], skipped: [] };
    // Written in another file (a thread read after a fork): its before-image would overwrite this one's cells (D13).
    if (rec.other_workbook) return { error: `This change was made in “${rec.other_workbook}”: open that file to undo it.`, code: 'UndoOtherWorkbook', retryable: false, hint: '' };
    if (rec.stale) return { error: 'The sheet changed since this write; the saved before-image no longer applies.', code: 'UndoStale', retryable: false, hint: '' };
    return runner.restoreFromRecord(rec);
  }

  /** After a batch, converge every circular block a successful write touched (domain #35), once each. */
  private async autoConverge(executions: { tool_name: string; result: ToolResult }[]): Promise<void> {
    if (!this.deps.circularBlocks || !this.deps.runLocal) return;
    const writes = executions.filter((e) => e.tool_name !== 'excel_converge' && !isToolError(e.result) && changesOf(e.result));
    if (!writes.length) return;
    let blocks: CircularBlock[] = [];
    try {
      blocks = await this.deps.circularBlocks();
    } catch {
      return;
    }
    const todo = new Map<string, CircularBlock>();
    for (const w of writes) for (const b of touchedCircularBlocks(blocks, changesOf(w.result))) todo.set(b.block_id, b);
    const converged = new Set<string>();
    for (const b of todo.values()) {
      const result = await this.deps.runLocal('excel_converge', { sheet: b.sheet, live_range: b.live_range, circ_range: b.circ_range, switch_cell: b.switch_cell, delta_cell: b.delta_cell, tolerance: b.tolerance, max_iterations: b.max_iterations });
      this.deps.onConverged?.({ block_id: b.block_id, result });
      if (!isToolError(result)) converged.add(b.sheet);
    }
    // Converging writes the block's values: the index captures those sheets too (they are filtered out of the
    // change events as the add-in's own).
    if (converged.size) this.deps.onSheetsWritten?.([...converged]);
  }

  /** Which sheets this batch changed. The fingerprint misses what a write changes without touching a formula or
   *  a constant (formats, styles, recalculated values), but the calls that ran say where they wrote. Only writes
   *  count: a read or a navigation names a sheet too, and counting it re-captured that sheet and every sheet
   *  reading it after a batch that changed nothing. */
  private reportWrittenSheets(executions: ToolExecution[]): void {
    const sheets = new Set<string>();
    for (const exec of executions) {
      if (isToolError(exec.result) || exec.result.status === 'cancelled') continue;
      const risk = TOOL_CATALOGUE[exec.tool_name]?.risk;
      const writes = risk === 'write' || risk === 'destructive';
      const sheet = changesOf(exec.result)?.sheet ?? (writes ? exec.target?.sheet : null) ?? null;
      if (sheet) sheets.add(sheet);
    }
    if (sheets.size) this.deps.onSheetsWritten?.([...sheets]);
  }

  /** Write-through of the before-images of a batch (domain #38f). Resolves once they are filed, or after
   *  `UNDO_FILING_WAIT_MS`: the server reads them as soon as it has the results, to take a batch back in the turn that
   *  wrote it. The same fingerprint serves a whole sheet, read once (`known`: those the batch already reports): each
   *  write read all of a large sheet for it. A post that fails is tried once more, it is idempotent on the journal id;
   *  one that fails again is said, not only logged: no undo will find it. */
  private async persistJournal(known: Record<string, string>, executions: { journal_id: string | null; tool_name: string; target: { sheet: string; address: string } | null }[]): Promise<void> {
    const threadId = this.state.threadId;
    const api = this.deps.api;
    const runner = this.deps.runner;
    if (!threadId || !api.saveUndo || !runner.journalEntry) return;
    // The turn that wrote them: an entry undone opens again the audit fixes of its turn (sculpt-api `report.reopen`).
    const turnIndex = [...this.state.messages].reverse().find((m) => m.role === 'assistant')?.turnIndex ?? null;
    const fingerprints = new Map<string, Promise<string | null>>();
    const fingerprintOf = (sheet: string): Promise<string | null> => {
      let fingerprint = fingerprints.get(sheet);
      if (!fingerprint) {
        fingerprint = known[sheet] ? Promise.resolve(known[sheet])
          : this.deps.sheetFingerprint ? this.deps.sheetFingerprint(sheet).catch(() => null) : Promise.resolve(null);
        fingerprints.set(sheet, fingerprint);
      }
      return fingerprint;
    };
    const todo = executions.flatMap((exec) => {
      const entry = exec.journal_id ? runner.journalEntry!(exec.journal_id) : null;
      return entry ? [{ entry, tool_name: exec.tool_name }] : [];
    });
    let notFiled = 0;
    const file = async ({ entry, tool_name }: (typeof todo)[number]): Promise<void> => {
      const sheet = entry.before.sheet;
      const snapshots = [entry.before, ...(entry.extra ?? [])];
      const record = {
        thread_id: threadId,
        workbook_id: this.deps.getWorkbookId?.() ?? null,
        journal_id: entry.id,
        tool_name,
        sheet,
        fingerprint: await fingerprintOf(sheet),
        ranges: snapshots.map((s) => `${s.sheet}!${s.address}`),
        before_image: { snapshots },
        inserted_rows: null,
        undone: false,
        turn_index: turnIndex,
      };
      try {
        await api.saveUndo!(record);
      } catch {
        await new Promise((resolve) => setTimeout(resolve, UNDO_FILING_RETRY_MS));
        try {
          await api.saveUndo!(record);
        } catch (err) {
          notFiled += 1;
          console.warn('[agent-v2] undo write-through failed', err);
        }
      }
    };
    const queue = [...todo];
    const worker = async (): Promise<void> => {
      for (let next = queue.shift(); next; next = queue.shift()) await file(next);
    };
    const filing = Promise.all(Array.from({ length: Math.min(UNDO_FILED_AT_ONCE, queue.length) }, worker)).then(() => {
      if (notFiled) this.set({ error: UNDO_NOT_FILED_TEXT });
    });
    let timer: ReturnType<typeof setTimeout> | undefined;
    const waited = new Promise<void>((resolve) => { timer = setTimeout(resolve, UNDO_FILING_WAIT_MS); });
    await Promise.race([filing, waited]);
    clearTimeout(timer);
  }

  private patchAnyCard(toolCallId: string, fn: (c: ToolCard) => ToolCard): void {
    this.set({ messages: this.state.messages.map((m) => (m.toolCards?.some((c) => c.tool_call_id === toolCallId) ? mapCard(m, toolCallId, fn) : m)) });
  }

  // ---- stream reducer ------------------------------------------------------------------
  /** `allowed`: the index refusals (409 before the stream starts) `sendAfterIndex` may still answer with a wait on
   *  this turn; any other refusal is reported. */
  private async consume(stream: AsyncGenerator<SSEEvent>, allowed: IndexWaits = NO_INDEX_WAIT): Promise<void> {
    this.set({ streaming: true });
    this.setActivity('model');
    let batch: ClientToolBatch | null = null;
    let awaiting = false;
    // The server aborted the run after a long silence (its `error` + `done` follow): the run is parked at its
    // last checkpoint and is woken below instead of being reported as a failure.
    let stalled = false;
    try {
      for await (const { event, data } of stream) {
        switch (event) {
          case 'stalled':
            stalled = true;
            break;
          case 'thread_id':
            if (data && data !== this.state.threadId) {
              this.set({ threadId: data });
              this.deps.onThreadChanged?.(data);
            }
            break;
          case 'turn': {
            // #41 key of the turn being streamed: what a per-message rating names. /resume re-sends the
            // same value for the second half of a turn, so the identity guard keeps it a no-op.
            const info = safeParse<TurnStarted>(data);
            if (typeof info?.turn_index === 'number') {
              this.patchLastAssistant((m) => (m.turnIndex === info.turn_index ? m : { ...m, turnIndex: info.turn_index }));
            }
            break;
          }
          case 'text':
            this.patchLastAssistant((m) => appendText(m, data));
            break;
          case 'tool_call': {
            const info = safeParse<ToolCallStarted>(data);
            if (info?.tool_call_id) {
              // The arguments come with the announcement now, so a read step can say which sheet it is about
              // rather than only which tool is running.
              const input = info.input ?? {};
              this.patchLastAssistant((m) => appendCards(m, [{ tool_call_id: info.tool_call_id, tool_name: info.tool_name, input, preview: '', risk: 'read', requires_confirmation: false, status: 'running', target: toolTarget(info.tool_name, input), server: true, agent: info.agent }]));
            }
            break;
          }
          case 'plan': {
            // The accepted Change Plan, between the model's answer and the batch the add-in runs.
            const view = safeParse<PlanView>(data);
            if (view) this.patchLastAssistant((m) => ({ ...m, plan: view }));
            break;
          }
          case 'manual': {
            // A manual write declared before any of it runs (sculpt-api `start_manual`): drawn above the batch.
            const declared = safeParse<ManualDeclaration>(data);
            if (declared && Array.isArray(declared.sheets)) this.patchLastAssistant((m) => ({ ...m, manual: declared }));
            break;
          }
          case 'tool_result': {
            const info = safeParse<ServerToolResult>(data);
            if (info?.tool_call_id) {
              // A refusal the agent is told to fix (`retryable`: a read during an operation, a plan to resubmit) is
              // its own next step, not a failure: on the local test of 2026-10-08 six of them read as six errors.
              const failed = isToolError(info.result);
              const retried = failed && (info.result as { retryable?: unknown }).retryable === true;
              this.patchCard(info.tool_call_id, (c) => ({ ...c, status: retried ? 'cancelled' : failed ? 'error' : 'done',
                                                          error: failed ? String(info.result.error) : null, retried }));
            }
            // The structural audit's card: the latest call wins (the one after the model's verdicts). The drivers'
            // card likewise: the latest `drivers` call that drew one.
            if (info?.audit) {
              // An undo's card opens fixes again: the older cards of the thread say so too, as a reloaded thread does.
              const reopened = info.tool_name === 'undo_operation' ? turnsReopenedBy(this.state.messages, info.audit) : [];
              this.patchLastAssistant((m) => ({ ...m, audit: info.audit }));
              for (const turn of reopened) this.reopenFixes(turn, false);
            }
            if (info?.drivers) this.patchLastAssistant((m) => ({ ...m, drivers: info.drivers }));
            if (info?.metrics) this.patchLastAssistant((m) => ({ ...m, metrics: info.metrics }));
            if (info?.model_map) this.patchLastAssistant((m) => ({ ...m, model_map: info.model_map }));
            if (info?.terms) this.patchLastAssistant((m) => ({ ...m, terms: info.terms }));
            break;
          }
          case 'client_tool_call':
            batch = safeParse<ClientToolBatch>(data);
            // An undo with fingerprints to check is drawn after the stream, once the check passed (`resumeIfStale`).
            if (batch && !checksFirst(batch)) this.attachBatch(batch);
            break;
          case 'awaiting_client':
            awaiting = true;
            break;
          case 'usage': {
            const u = safeParse<UsageSummary>(data);
            if (u) this.deps.onUsage?.(u);
            break;
          }
          case 'service_interrupted':
          case 'unavailable':
          case 'error': {
            if (stalled) break; // handled after the stream: woken, or reported once the wakes are used up
            // `service_interrupted` is shown in the pane's own words, never the server's: the user learns only
            // that the team is restoring the service.
            const text = event === 'service_interrupted' ? this.deps.onServiceInterrupted?.() || SERVICE_INTERRUPTED_TEXT : data;
            this.patchLastAssistant((m) => ({ ...(m.content ? m : appendText(m, text || 'The assistant is unavailable.')), error: true, pending: false, activity: 'idle' }));
            this.set({ error: text || 'error' });
            break;
          }
          case 'done':
            if (stalled) break;
            this.patchLastAssistant((m) => ({ ...m, pending: false, activity: 'idle' }));
            this.syncAuditSheet([...this.state.messages].reverse().find((m) => m.role === 'assistant'));
            break;
          default:
            break;
        }
      }
    } catch (err) {
      const wait = indexRefusalOf(err);
      const hook = wait === 'rebuild' ? this.deps.refreshIndex : wait === 'understanding' ? this.deps.awaitUnderstanding : undefined;
      if (wait && allowed[wait] && hook) {
        this.indexRefused = wait;
        this.patchLastAssistant((m) => ({ ...m, activity: wait === 'rebuild' ? 'index' : 'understanding' }));
      } else if (!(err instanceof DOMException && err.name === 'AbortError')) {
        const msg = this.describeRefusal(err);
        this.patchLastAssistant((m) => ({ ...(m.content ? m : appendText(m, msg)), error: true, pending: false, activity: 'idle' }));
        this.set({ error: msg });
      }
    } finally {
      this.set({ streaming: false });
    }
    if (stalled) {
      await this.wake();
      return;
    }
    if (awaiting && !batch) {
      this.patchLastAssistant((m) => ({ ...(m.content ? m : appendText(m, 'The server is waiting for tool results but sent no batch.')), error: true, pending: false, activity: 'idle' }));
      return;
    }
    if (batch) {
      if (checksFirst(batch)) {
        if (await this.resumeIfStale(batch)) return;
        this.attachBatch(batch);
      }
      await this.executeIfDecided();
    }
  }

  /** The message for a turn that failed before or while streaming: the refusals the user can act on in words,
   *  anything else as it came. The user's message stays in its bubble, as for every refusal before the stream. */
  private describeRefusal(err: unknown): string {
    if (isModelRefusal(err)) return this.deps.onModelRefused?.() || MODEL_REFUSED_TEXT;
    const limit = turnsInProgressLimit(err);
    if (limit !== null) return this.deps.onTurnsInProgress?.(limit) || turnsInProgressText(limit);
    return err instanceof Error ? err.message : String(err);
  }

  /** After a `stalled` event: re-enter the run from its last checkpoint (`/continue`), at most
   *  MAX_WAKES_PER_TURN times per turn; then report the stall as an error. */
  private async wake(): Promise<void> {
    const threadId = this.state.threadId;
    const signal = this.abort?.signal;
    if (signal?.aborted) return; // stop() already ended the turn
    if (threadId && this.deps.api.continueThread && this.wakes < MAX_WAKES_PER_TURN) {
      this.wakes += 1;
      console.warn(`[agent-v2] run stalled: waking thread ${threadId} (${this.wakes}/${MAX_WAKES_PER_TURN})`);
      if (await this.wakeStream(threadId, signal)) return;
    }
    this.patchLastAssistant((m) => ({ ...(m.content ? m : appendText(m, STALLED_TEXT)), error: true, pending: false, activity: 'idle' }));
    this.set({ error: STALLED_TEXT });
  }

  /** Open the wake stream and hand it to the reducer. False when the server could not be woken at all — a
   *  server without the route, a 503, a dropped connection — so the caller reports the stall instead. The
   *  first event is pulled here for that: past it the stream is the reducer's, and a `/continue` that
   *  answers `error` is a server message the user should read, not a failure to reach the server. */
  private async wakeStream(threadId: string, signal: AbortSignal | undefined): Promise<boolean> {
    const stream = this.deps.api.continueThread!(threadId, signal);
    let first: IteratorResult<SSEEvent>;
    try {
      first = await stream.next();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return true; // stop() ended the turn
      if (turnsInProgressLimit(err) !== null) {
        // Not a failure to reach the server: the wake is refused while the user's other turns stream.
        const msg = this.describeRefusal(err);
        this.patchLastAssistant((m) => ({ ...(m.content ? m : appendText(m, msg)), error: true, pending: false, activity: 'idle' }));
        this.set({ error: msg });
        return true;
      }
      console.warn('[agent-v2] wake failed', err);
      return false;
    }
    if (first.done) return false; // a stream that closed without a single event tells the user nothing
    await this.consume(withFirst(first.value, stream));
    return true;
  }

  /** Create the cards for a batch and register the decisions it needs. */
  private attachBatch(batch: ClientToolBatch): void {
    if (isQuestionBatch(batch)) {
      this.attachQuestions(batch);
      return;
    }
    if (batch.kind === 'undo') {
      this.attachUndo(batch);
      return;
    }
    const mode = this.deps.getMode();
    if (batch.calls.some((call) => needsApproval(call, mode))) {
      this.attachApproval(batch);
      return;
    }
    const decisions = new Map<string, boolean | undefined>();
    const previews: ClientToolCall[] = [];
    const cards: ToolCard[] = batch.calls.map((call) => {
      const confirm = needsApproval(call, mode);
      // An in-place block write is previewed first (#36): its decision waits for the overwrite count.
      const preview = call.tool_name === 'excel_write_block' && mode !== 'full_auto' && !!this.deps.runner.previewCall;
      if (preview) previews.push(call);
      decisions.set(call.tool_call_id, confirm || preview ? undefined : true);
      return { tool_call_id: call.tool_call_id, tool_name: call.tool_name, input: call.input ?? {}, preview: call.preview, risk: call.risk, requires_confirmation: confirm, status: confirm ? 'awaiting_confirmation' : 'pending', target: toolTarget(call.tool_name, call.input ?? {}), agent: call.agent, protected: call.protected };
    });
    this.pending = { batch, decisions, acks: new Map() };
    this.patchLastAssistant((m) => ({ ...appendCards(m, cards), pending: true, activity: 'excel', pendingBatch: batch }));
    this.set({ busy: true });
    for (const call of previews) void this.previewOverwrite(call, batch);
  }

  /** A batch with edits to approve (ask mode; a destructive call in auto): ONE card for the whole batch, the edits
   *  listed inside it, Accept edits or Reject — all or nothing. A Change Plan is one change, written, verified and
   *  undone as one: approving three of its five writes left a revenue line that no total read, or a total that read
   *  a line never written. The calls' steps are drawn once the edits are accepted, to show them running; a call's
   *  own card from a reloaded thread gives way to the batch's. */
  private attachApproval(batch: ClientToolBatch): void {
    const decisions = new Map<string, boolean | undefined>();
    const steps: ToolCard[] = batch.calls.map((call) => {
      decisions.set(call.tool_call_id, undefined);
      return { tool_call_id: call.tool_call_id, tool_name: call.tool_name, input: call.input ?? {}, preview: call.preview, risk: call.risk, requires_confirmation: false, status: 'pending', target: toolTarget(call.tool_name, call.input ?? {}), agent: call.agent, protected: call.protected };
    });
    const edits = steps.filter((c) => c.risk !== 'read');
    const risk = edits.some((c) => c.risk === 'destructive') ? 'destructive' : 'write';
    const card: ToolCard = { tool_call_id: batch.batch_id, tool_name: APPROVE_BATCH, input: { edits }, preview: '', risk, requires_confirmation: true, status: 'awaiting_confirmation', protected: edits.some((c) => c.protected) };
    const ids = new Set(steps.map((c) => c.tool_call_id));
    this.pending = { batch, decisions, acks: new Map(), aggregate: batch.batch_id, steps };
    this.patchLastAssistant((m) => ({ ...appendCards(removeCards(m, ids), [card]), pending: true, activity: 'excel', pendingBatch: batch }));
    this.set({ busy: true });
  }

  /** The server's undo of an operation (sculpt-api operations/undo.py): the calls are drawn as steps and ONE
   *  card — keyed on the batch id — approves or declines them all. Forty cards for one decision is not a
   *  decision; and a half-approved undo would leave the workbook in a third state, which is what the undo
   *  exists to prevent. Every call waits on that card whatever its own flag says. */
  private attachUndo(batch: ClientToolBatch): void {
    const decisions = new Map<string, boolean | undefined>();
    const steps: ToolCard[] = batch.calls.map((call) => {
      decisions.set(call.tool_call_id, undefined);
      return { tool_call_id: call.tool_call_id, tool_name: call.tool_name, input: call.input ?? {}, preview: call.preview, risk: call.risk, requires_confirmation: false, status: 'pending', target: toolTarget(call.tool_name, call.input ?? {}), agent: call.agent, protected: call.protected };
    });
    const summary = (batch.summary ?? {}) as unknown as Record<string, unknown>;
    const card: ToolCard = { tool_call_id: batch.batch_id, tool_name: 'undo_operation', input: summary, preview: '', risk: 'destructive', requires_confirmation: true, status: 'awaiting_confirmation' };
    this.pending = { batch, decisions, acks: new Map(), aggregate: batch.batch_id };
    this.patchLastAssistant((m) => ({ ...appendCards(m, [card, ...steps]), pending: true, activity: 'excel', pendingBatch: batch }));
    this.set({ busy: true });
  }

  /** A Question card (sculpt-api PLAN-questions.md): nothing to run and nothing to approve — the turn waits
   *  on the answers. The card is marked `requires_confirmation` so it keeps its place in the thread once
   *  answered, where it shows the recap of what was answered. */
  private attachQuestions(batch: ClientToolBatch): void {
    const call = batch.calls[0];
    const card: ToolCard = { tool_call_id: call.tool_call_id, tool_name: call.tool_name, input: call.input ?? {},
                             preview: call.preview, risk: call.risk, requires_confirmation: true, status: 'awaiting_confirmation' };
    this.questions = { batch, call, draft: [] };
    this.patchLastAssistant((m) => ({ ...appendCards(m, [card]), pending: true, activity: 'idle', pendingBatch: batch }));
    this.set({ busy: true, awaitingAnswers: true });
  }

  /** The user answered the card: the answers go back as the tool's result and the same turn goes on. An
   *  untouched question carries the agent's own suggestion, marked `left_to_sculpt`. */
  async answerQuestions(answers: QuestionAnswer[], message?: string): Promise<void> {
    const q = this.questions;
    if (!q) return;
    this.questions = null;
    const said = message?.trim() || null;
    this.patchCard(q.call.tool_call_id, (c) => ({ ...c, status: 'done', answers, answerMessage: said }));
    this.patchLastAssistant((m) => ({ ...m, pendingBatch: null, activity: 'model' }));
    const threadId = this.state.threadId;
    if (!threadId) {
      this.set({ busy: false, awaitingAnswers: false, error: 'No thread to resume' });
      this.setActivity('idle');
      return;
    }
    // One change, so the agent never reads as stopped between the answer and the stream that resumes the turn.
    this.set({ busy: false, awaitingAnswers: false, streaming: true });
    const results: ToolResultEntry[] = [{ tool_call_id: q.call.tool_call_id, result: { answers, ...(said ? { message: said } : {}) } }];
    await this.consume(this.deps.api.resume({ thread_id: threadId, batch_id: q.batch.batch_id, results }, this.abort?.signal));
  }

  /** The user cancels the card instead of answering it: the turn resumes with the call declined, so the agent
   *  learns it — and an operation that was waiting on the answer is abandoned server-side, never guessed. */
  async cancelQuestions(): Promise<void> {
    const q = this.questions;
    if (!q) return;
    this.questions = null;
    this.patchCard(q.call.tool_call_id, (c) => ({ ...c, status: 'cancelled' }));
    this.patchLastAssistant((m) => ({ ...m, pendingBatch: null, activity: 'model' }));
    const threadId = this.state.threadId;
    if (!threadId) {
      this.set({ busy: false, awaitingAnswers: false, error: 'No thread to resume' });
      this.setActivity('idle');
      return;
    }
    // One change, so the agent never reads as stopped between the answer and the stream that resumes the turn.
    this.set({ busy: false, awaitingAnswers: false, streaming: true });
    const results: ToolResultEntry[] = [{ tool_call_id: q.call.tool_call_id, result: { status: 'cancelled', reason: 'declined' } }];
    await this.consume(this.deps.api.resume({ thread_id: threadId, batch_id: q.batch.batch_id, results }, this.abort?.signal));
  }

  /** Pre-flight of a block write: with overwrites the card must be acknowledged (in ask AND auto, #36);
   *  without, an unconfirmed call proceeds as decided by the policy. */
  private async previewOverwrite(call: ClientToolCall, batch: ClientToolBatch): Promise<void> {
    let info: { cells: number; address: string; sample: string[] } | null = null;
    try {
      info = (await this.deps.runner.previewCall?.(call)) ?? null;
    } catch {
      info = null;
    }
    const p = this.pending;
    if (!p || p.batch !== batch || !p.decisions.has(call.tool_call_id)) return;
    const mode = this.deps.getMode();
    const confirm = needsApproval(call, mode);
    if (info && info.cells > 0) {
      this.patchCard(call.tool_call_id, (c) => ({ ...c, overwrite: info, requires_confirmation: true, status: 'awaiting_confirmation' }));
      return; // waits for decide(..., { overwrite_ack: true })
    }
    this.patchCard(call.tool_call_id, (c) => ({ ...c, overwrite: info ?? { cells: 0, address: '', sample: [] } }));
    if (!confirm) {
      p.decisions.set(call.tool_call_id, true);
      void this.executeIfDecided();
    }
  }

  /** When every decision is in, run the batch (in tool order) and resume the graph. */
  private async executeIfDecided(): Promise<void> {
    const p = this.pending;
    if (!p) return;
    for (const d of p.decisions.values()) if (d === undefined) return;
    this.pending = null;
    this.patchLastAssistant((m) => ({ ...m, pendingBatch: null, activity: 'excel' }));
    const signal = this.abort?.signal;
    const fullAuto = this.deps.getMode() === 'full_auto';
    const approved: ClientToolCall[] = p.batch.calls
      .filter((c) => p.decisions.get(c.tool_call_id) === true)
      // The overwrite acknowledgement comes from the user's checkbox (or full_auto), never from the model (#36).
      .map((c) => (c.tool_name === 'excel_write_block' ? { ...c, input: { ...(c.input ?? {}), overwrite_ack: p.acks.get(c.tool_call_id) === true || fullAuto } } : c));
    // An undo runs on the sheets as its operation left them, or not at all: restored at coordinates that have moved,
    // a before-image writes over the modeller's own changes (sculpt-api PLAN-operations-fixes.md point 9).
    // Checked before the card was drawn, and again here: the sheet may have been edited while the card waited.
    const changed = approved.length ? await this.changedSheets(p.batch.expected_fingerprints) : [];
    if (changed.length) {
      await this.resumeStale(p.batch, changed, p.aggregate);
      return;
    }
    const executions = await this.deps.runner.runSequential(approved, signal, {
      onStart: (call) => this.patchCard(call.tool_call_id, (c) => ({ ...c, status: 'running' })),
      onDone: (exec: ToolExecution) => {
        // The batch's one card counts what its edits cost and which failed, once they have run.
        if (p.aggregate) this.patchCard(p.aggregate, (c) => withEditResult(c, exec));
        this.patchCard(exec.tool_call_id, (c) => ({
          ...c,
          status: isToolError(exec.result) ? 'error' : exec.result.status === 'cancelled' ? 'cancelled' : 'done',
          error: isToolError(exec.result) ? String(exec.result.error) : null,
          hint: isToolError(exec.result) && typeof exec.result.hint === 'string' ? exec.result.hint : null,
          journal_id: exec.journal_id,
          changes: changesOf(exec.result),
          target: exec.target ?? c.target,
        }));
      },
    }, { stopOnError: p.batch.stop_on_error === true });
    this.reportWrittenSheets(executions);
    await this.autoConverge(executions);
    // Taken once the batch ran and its circular blocks converged: the state an undo of it expects to find again.
    const fingerprints = p.batch.report_fingerprints ? await this.fingerprintsOf(executions) : {};
    // Before the results, which can ask the server to take the batch back at once.
    await this.persistJournal(fingerprints, executions);
    const byId = new Map(executions.map((e) => [e.tool_call_id, e]));
    // Results in the batch's order; rejected calls report `cancelled` so the history stays valid. Each executed
    // call carries how long Excel took, so the server can see where a batch's time went.
    const results: ToolResultEntry[] = p.batch.calls.map((c) => {
      const exec = byId.get(c.tool_call_id);
      const declined = p.declined ? { ...CANCELLED_RESULT, message: p.declined } : CANCELLED_RESULT;
      return exec ? { tool_call_id: c.tool_call_id, result: exec.result, duration_ms: exec.duration_ms } : { tool_call_id: c.tool_call_id, result: declined };
    });
    if (signal?.aborted) {
      this.set({ busy: false });
      return; // stop() already resolved the interrupt server-side
    }
    const threadId = this.state.threadId;
    if (!threadId) {
      this.set({ busy: false, error: 'No thread to resume' });
      this.setActivity('idle');
      return;
    }
    // One change, so the agent never reads as stopped between its batch and the stream that resumes the turn:
    // the index would start capturing in that gap.
    this.set({ busy: false, streaming: true });
    this.setActivity('model'); // the gap between local execution and the new stream
    await this.consume(this.deps.api.resume({ thread_id: threadId, batch_id: p.batch.batch_id, results, ...(Object.keys(fingerprints).length ? { fingerprints } : {}) }, signal));
  }

  /** An undo whose sheets changed since its operation is answered before its card is drawn: approving it could only
   *  end in the same refusal. True when the batch was resumed that way. */
  private async resumeIfStale(batch: ClientToolBatch): Promise<boolean> {
    if (!checksFirst(batch)) return false;
    this.setActivity('excel');
    const changed = await this.changedSheets(batch.expected_fingerprints);
    if (!changed.length) return false;
    await this.resumeStale(batch, changed);
    return true;
  }

  /** Resume an undo with every call answered `UndoStale`: nothing ran, and the server's reply says why. */
  private async resumeStale(batch: ClientToolBatch, changed: string[], aggregate?: string): Promise<void> {
    const stale: ToolResult = { error: `${changed.join(', ')} changed since the operation was made`, code: 'UndoStale', retryable: false, hint: '', sheet: changed.join(', ') };
    if (aggregate) this.patchCard(aggregate, (c) => ({ ...c, status: 'error', error: String(stale.error) }));
    for (const c of batch.calls) this.patchCard(c.tool_call_id, (card) => ({ ...card, status: 'cancelled' }));
    const threadId = this.state.threadId;
    if (!threadId) {
      this.set({ busy: false, error: 'No thread to resume' });
      return;
    }
    this.set({ busy: false, streaming: true });
    this.setActivity('model');
    const results = batch.calls.map((c) => ({ tool_call_id: c.tool_call_id, result: stale }));
    await this.consume(this.deps.api.resume({ thread_id: threadId, batch_id: batch.batch_id, results }, this.abort?.signal));
  }

  /** The sheets whose live fingerprint differs from the one expected — none when nothing is expected or no sheet can
   *  be fingerprinted here (a read that fails is ignorance, not evidence of a change). */
  private async changedSheets(expected: Record<string, string> | undefined): Promise<string[]> {
    if (!expected || !this.deps.sheetFingerprint) return [];
    const out: string[] = [];
    for (const [sheet, fingerprint] of Object.entries(expected)) {
      const now = await this.deps.sheetFingerprint(sheet).catch(() => null);
      if (now && fingerprint && now !== fingerprint) out.push(sheet);
    }
    return out;
  }

  /** The fingerprint of every sheet the batch wrote (a write that ran, not a read), as they are now. */
  private async fingerprintsOf(executions: ToolExecution[]): Promise<Record<string, string>> {
    if (!this.deps.sheetFingerprint) return {};
    const sheets = new Set<string>();
    for (const exec of executions) {
      if (isToolError(exec.result) || exec.result.status === 'cancelled') continue;
      const risk = TOOL_CATALOGUE[exec.tool_name]?.risk;
      if (risk !== 'write' && risk !== 'destructive') continue;
      const sheet = changesOf(exec.result)?.sheet ?? exec.target?.sheet;
      if (sheet) sheets.add(sheet);
    }
    const out: Record<string, string> = {};
    for (const sheet of sheets) {
      const fingerprint = await this.deps.sheetFingerprint(sheet).catch(() => null);
      if (fingerprint) out[sheet] = fingerprint;
    }
    return out;
  }
}

/** An undo carrying the fingerprints its sheets must still have: checked before its card is drawn
 *  (sculpt-api PLAN-operations-fixes.md point 9). */
function checksFirst(batch: ClientToolBatch): boolean {
  return batch.kind === 'undo' && Object.keys(batch.expected_fingerprints ?? {}).length > 0;
}

/** How a reloaded call is drawn.
 *
 *  `pending` only when the server says so — a batch the pane was closed in the middle of. A call with no
 *  status at all is finished: nothing is executing while a thread is being read back, and defaulting it to
 *  `pending` is what made every past turn come back as a run still in progress, down to a forty-eight-call
 *  plan showing a progress bar stuck at 0 of 48. */
function reloadedStatus(status: DisplayToolStatus | undefined): ToolCard['status'] {
  switch (status) {
    case 'error':
      return 'error';
    case 'cancelled':
      return 'cancelled';
    case 'pending':
      return 'pending';
    default:
      return 'done';
  }
}

/** The turn ratings a reloaded thread comes back with, put on the assistant messages of their turns. */
export function withRatings(messages: ChatMessage[], ratings: TurnRating[]): ChatMessage[] {
  if (!ratings.length) return messages;
  const byTurn = new Map(ratings.map((r) => [r.turn_index, r]));
  return messages.map((m) => {
    const rating = m.role === 'assistant' && m.turnIndex != null ? byTurn.get(m.turnIndex) : undefined;
    return rating ? { ...m, rating } : m;
  });
}

/** A reloaded turn is one message per model answer, so its cards sit on the messages of the calls that drew them
 *  (`structural_audit`, `drivers`, `metrics`, `model_map`, `reconcile_terms`), which have no text — and the audit call before the verdicts carries a card too.
 *  Live, the turn is one message with the latest card of each kind under the answer: keep only the turn's latest
 *  card of each kind, on its last assistant message. */
export function latestCardsPerTurn(messages: ChatMessage[]): ChatMessage[] {
  const out = [...messages];
  let turnStart = 0;
  for (let i = 0; i <= out.length; i += 1) {
    if (i < out.length && out[i].role !== 'user') continue;
    const turn = out.slice(turnStart, i).map((m, k) => ({ m, at: turnStart + k })).filter(({ m }) => m.role === 'assistant');
    for (const key of ['audit', 'drivers', 'metrics', 'model_map', 'terms'] as const) {
      const latest = [...turn].reverse().find(({ at }) => out[at][key])?.at;
      if (latest === undefined) continue;
      const card = out[latest][key];
      for (const { at } of turn) if (out[at][key]) out[at] = { ...out[at], [key]: undefined };
      const last = turn[turn.length - 1].at;
      out[last] = { ...out[last], [key]: card };
    }
    turnStart = i + 1;
  }
  return out;
}

/** A reloaded answer's edits, settled, as the one card they were approved or rejected with live
 *  (`ChatSession.attachApproval`): rejected when none ran, applied otherwise. A call still pending is left as it
 *  is: its batch is re-attached and draws the card itself. */
function asApproved(cards: ToolCard[]): ToolCard[] {
  const edits = cards.filter((c) => c.requires_confirmation && c.risk !== 'read' && c.tool_name !== TOOL_ASK_USER && c.status !== 'pending');
  if (!edits.length) return cards;
  const card: ToolCard = { tool_call_id: `${APPROVE_BATCH}:${edits[0].tool_call_id}`, tool_name: APPROVE_BATCH, input: { edits },
                           preview: '', risk: edits.some((c) => c.risk === 'destructive') ? 'destructive' : 'write', requires_confirmation: true,
                           status: edits.every((c) => c.status === 'cancelled') ? 'cancelled' : 'done' };
  const at = cards.indexOf(edits[0]);
  const rest = cards.filter((c) => !edits.includes(c));
  return [...rest.slice(0, at), card, ...rest.slice(at)];
}

export function displayToChat(m: DisplayMessage): ChatMessage {
  const cards: ToolCard[] = (m.tool_calls ?? []).map((tc) => ({
      tool_call_id: tc.tool_call_id,
      tool_name: tc.tool_name,
      input: tc.input ?? {},
      preview: '',
      risk: tc.risk ?? 'read',
      requires_confirmation: tc.requires_confirmation ?? false,
      status: reloadedStatus(tc.status),
      target: toolTarget(tc.tool_name, tc.input ?? {}),
      changes: tc.changes ?? null,
      error: tc.error ?? null,
      hint: tc.hint ?? null,
      agent: tc.agent,
      answers: tc.answers ?? null,
      answerMessage: tc.message ?? null,
    }));
  // History carries no ordering between text and tool calls: text first, then the tool run.
  const segments: MessageSegment[] = [];
  if (m.content) segments.push({ kind: 'text', markdown: m.content });
  const shown = asApproved(cards);
  if (shown.length) segments.push({ kind: 'tools', cards: shown });
  const documents = m.role === 'user' && m.documents?.length ? m.documents : undefined;
  // The cards of the turn: the last `structural_audit` call that drew one, the last `drivers` call that drew one.
  const audit = [...(m.tool_calls ?? [])].reverse().find((tc) => tc.audit)?.audit ?? undefined;
  const drivers = [...(m.tool_calls ?? [])].reverse().find((tc) => tc.drivers)?.drivers ?? undefined;
  const metrics = [...(m.tool_calls ?? [])].reverse().find((tc) => tc.metrics)?.metrics ?? undefined;
  const modelMap = [...(m.tool_calls ?? [])].reverse().find((tc) => tc.model_map)?.model_map ?? undefined;
  const terms = [...(m.tool_calls ?? [])].reverse().find((tc) => tc.terms)?.terms ?? undefined;
  const manual = [...(m.tool_calls ?? [])].reverse().find((tc) => tc.manual)?.manual ?? undefined;
  return { id: nextId(), role: m.role, content: m.content, segments: m.role === 'assistant' ? segments : undefined, toolCards: shown,
           turnIndex: m.turn_index ?? undefined, ...(m.duration_ms != null ? { turnMs: m.duration_ms } : {}), ...(m.plan ? { plan: m.plan } : {}), ...(audit ? { audit } : {}),
           ...(drivers ? { drivers } : {}), ...(metrics ? { metrics } : {}),
           ...(modelMap ? { model_map: modelMap } : {}), ...(terms ? { terms } : {}), ...(manual ? { manual } : {}),
           ...(documents ? { documents } : {}) };
}

// ---- nested steps (§11.5) ---------------------------------------------------------------

/** A run of consecutive steps: `agent` is null for the main agent (rendered flat) or the sub-agent
 *  name (rendered as an agent row with its steps indented underneath). */
export interface StepGroup {
  agent: string | null;
  cards: ToolCard[];
  /** Aggregated status of the group: error if any step failed, active while any step is not settled,
   *  done when every step is done or cancelled. */
  status: 'done' | 'active' | 'error' | 'pending';
}

/** Group consecutive steps issued by the same sub-agent; main-agent steps stay ungrouped (one
 *  group per run, `agent: null`). Order is preserved. Pass only visible cards (`isCardVisible`)
 *  to get what the bubble shows: finished groups disappear, a group with running steps shows
 *  only those. */
export function groupSteps(cards: ToolCard[]): StepGroup[] {
  const groups: StepGroup[] = [];
  for (const card of cards) {
    const agent = isSubAgent(card.agent) ? card.agent : null;
    const last = groups[groups.length - 1];
    if (last && last.agent === agent) last.cards.push(card);
    else groups.push({ agent, cards: [card], status: 'pending' });
  }
  for (const g of groups) g.status = groupStatus(g.cards);
  return groups;
}

export function groupStatus(cards: ToolCard[]): StepGroup['status'] {
  if (cards.some((c) => c.status === 'error')) return 'error';
  if (cards.some((c) => c.status === 'running' || c.status === 'pending' || c.status === 'awaiting_confirmation')) return 'active';
  if (cards.length && cards.every((c) => c.status === 'done' || c.status === 'cancelled')) return 'done';
  return 'pending';
}
